import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Trash2, Plus, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MaterialReconciliation() {
  const [proHubRecords, setProHubRecords] = useState([]);
  const [jobWorkRecords, setJobWorkRecords] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('pro-hub');
  const [proHubProjectOpen, setProHubProjectOpen] = useState(false);
  const [jobWorkProjectOpen, setJobWorkProjectOpen] = useState(false);
  
  const [proHubForm, setProHubForm] = useState({
    type: 'pro-hub',
    project_number: '',
    westaw_project_number: '',
    supplier_id: '',
    invoice_number: '',
    date: new Date().toISOString().split('T')[0],
    invoice_value: '',
    weight: '',
    dc: ''
  });

  const [jobWorkForm, setJobWorkForm] = useState({
    westaw_project_number: '',
    dc: '',
    date: new Date().toISOString().split('T')[0],
    weight: '',
    invoice: '',
    value: '',
    supplier_id: '',
    remarks: '',
    transporter_name: '',
    transport_expense_send: '',
    transport_expense_return: ''
  });

  useEffect(() => {
    fetchProHubRecords();
    fetchJobWorkRecords();
    fetchSuppliers();
    fetchProjects();
  }, []);

  const fetchProHubRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/material-reconciliation/pro-hub`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProHubRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch records', error);
    }
  };

  const fetchJobWorkRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/material-reconciliation/job-work`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobWorkRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch records', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
    }
  };

  const handleProHubProjectSelect = (project) => {
    setProHubForm({
      ...proHubForm,
      project_number: project.client_project_number,
      westaw_project_number: project.westaw_project_number
    });
    setProHubProjectOpen(false);
  };

  const handleJobWorkProjectSelect = (project) => {
    setJobWorkForm({
      ...jobWorkForm,
      westaw_project_number: project.westaw_project_number
    });
    setJobWorkProjectOpen(false);
  };

  const getProHubProjectDisplay = () => {
    const selectedProject = projects.find(p => p.westaw_project_number === proHubForm.westaw_project_number);
    return selectedProject 
      ? `${selectedProject.westaw_project_number} - ${selectedProject.client_name}` 
      : 'Search and select a project...';
  };

  const getJobWorkProjectDisplay = () => {
    const selectedProject = projects.find(p => p.westaw_project_number === jobWorkForm.westaw_project_number);
    return selectedProject 
      ? `${selectedProject.westaw_project_number} - ${selectedProject.client_name}` 
      : 'Search and select a project...';
  };

  const handleProHubSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...proHubForm,
        date: new Date(proHubForm.date).toISOString(),
        invoice_value: parseFloat(proHubForm.invoice_value),
        weight: parseFloat(proHubForm.weight)
      };

      await axios.post(`${BACKEND_URL}/api/material-reconciliation/pro-hub`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Record created successfully');
      setProHubForm({
        type: activeTab === 'pro-hub' ? 'pro-hub' : 'container-bau',
        project_number: '',
        westaw_project_number: '',
        supplier_id: '',
        invoice_number: '',
        date: new Date().toISOString().split('T')[0],
        invoice_value: '',
        weight: '',
        dc: ''
      });
      fetchProHubRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleJobWorkSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...jobWorkForm,
        date: new Date(jobWorkForm.date).toISOString(),
        weight: parseFloat(jobWorkForm.weight),
        value: parseFloat(jobWorkForm.value),
        transport_expense_send: parseFloat(jobWorkForm.transport_expense_send) || 0,
        transport_expense_return: parseFloat(jobWorkForm.transport_expense_return) || 0
      };

      await axios.post(`${BACKEND_URL}/api/material-reconciliation/job-work`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Record created successfully');
      setJobWorkForm({
        westaw_project_number: '',
        dc: '',
        date: new Date().toISOString().split('T')[0],
        weight: '',
        invoice: '',
        value: '',
        supplier_id: '',
        remarks: '',
        transporter_name: '',
        transport_expense_send: '',
        transport_expense_return: ''
      });
      fetchJobWorkRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/material-reconciliation/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted successfully');
      fetchProHubRecords();
      fetchJobWorkRecords();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="material-reconciliation-title">
          Material Reconciliation
        </h1>
        <p className="text-slate-600 mt-2">Manage material reconciliation records</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="pro-hub" data-testid="pro-hub-tab">Pro-Hub</TabsTrigger>
          <TabsTrigger value="container-bau" data-testid="container-bau-tab">Container Bau</TabsTrigger>
          <TabsTrigger value="job-work" data-testid="job-work-tab">Job Work</TabsTrigger>
        </TabsList>

        {/* Pro-Hub Tab */}
        <TabsContent value="pro-hub">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Add Pro-Hub Record</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={(e) => { proHubForm.type = 'pro-hub'; handleProHubSubmit(e); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label>WESTAW Project</Label>
                    <Popover open={proHubProjectOpen} onOpenChange={setProHubProjectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={proHubProjectOpen}
                          className="w-full justify-between font-normal"
                          data-testid="pro-hub-project-select"
                        >
                          <span className={proHubForm.westaw_project_number ? "text-slate-900" : "text-slate-500"}>
                            {getProHubProjectDisplay()}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search projects..." />
                          <CommandList>
                            <CommandEmpty>No project found.</CommandEmpty>
                            <CommandGroup>
                              {projects.map((project) => (
                                <CommandItem
                                  key={project.id}
                                  value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                                  onSelect={() => handleProHubProjectSelect(project)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      proHubForm.westaw_project_number === project.westaw_project_number ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{project.westaw_project_number}</span>
                                    <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {proHubForm.westaw_project_number && (
                      <p className="text-xs text-slate-500">
                        Client Project: {proHubForm.project_number}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="supplier">Supplier</Label>
                    <Select value={proHubForm.supplier_id} onValueChange={(value) => setProHubForm({ ...proHubForm, supplier_id: value })} required>
                      <SelectTrigger data-testid="supplier-select">
                        <SelectValue placeholder="Select a supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_number">Invoice Number</Label>
                      <Input
                        id="invoice_number"
                        data-testid="invoice-number-input"
                        value={proHubForm.invoice_number}
                        onChange={(e) => setProHubForm({ ...proHubForm, invoice_number: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        data-testid="date-input"
                        type="date"
                        value={proHubForm.date}
                        onChange={(e) => setProHubForm({ ...proHubForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_value">Invoice Value</Label>
                      <Input
                        id="invoice_value"
                        data-testid="invoice-value-input"
                        type="number"
                        step="0.01"
                        value={proHubForm.invoice_value}
                        onChange={(e) => setProHubForm({ ...proHubForm, invoice_value: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="weight">Weight</Label>
                      <Input
                        id="weight"
                        data-testid="weight-input"
                        type="number"
                        step="0.01"
                        value={proHubForm.weight}
                        onChange={(e) => setProHubForm({ ...proHubForm, weight: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dc">DC</Label>
                    <Input
                      id="dc"
                      data-testid="dc-input"
                      value={proHubForm.dc}
                      onChange={(e) => setProHubForm({ ...proHubForm, dc: e.target.value })}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-pro-hub-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Recent Pro-Hub Records</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {proHubRecords.filter(r => r.type === 'pro-hub').slice(0, 10).map((record) => (
                    <div key={record.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">WESTAW: {record.westaw_project_number}</p>
                          <p className="text-sm text-slate-600">Project: {record.project_number}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div>Supplier: {record.supplier_name}</div>
                        <div>Invoice: {record.invoice_number}</div>
                        <div>Value: ₹{record.invoice_value}</div>
                        <div>Weight: {record.weight}</div>
                        <div className="col-span-2">Date: {new Date(record.date).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                  {proHubRecords.filter(r => r.type === 'pro-hub').length === 0 && (
                    <div className="text-center py-8 text-slate-600">No records found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Container Bau Tab - Same form as Pro-Hub */}
        <TabsContent value="container-bau">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Add Container Bau Record</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={(e) => { proHubForm.type = 'container-bau'; handleProHubSubmit(e); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label>WESTAW Project</Label>
                    <Popover open={proHubProjectOpen} onOpenChange={setProHubProjectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={proHubProjectOpen}
                          className="w-full justify-between font-normal"
                          data-testid="container-bau-project-select"
                        >
                          <span className={proHubForm.westaw_project_number ? "text-slate-900" : "text-slate-500"}>
                            {getProHubProjectDisplay()}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search projects..." />
                          <CommandList>
                            <CommandEmpty>No project found.</CommandEmpty>
                            <CommandGroup>
                              {projects.map((project) => (
                                <CommandItem
                                  key={project.id}
                                  value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                                  onSelect={() => handleProHubProjectSelect(project)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      proHubForm.westaw_project_number === project.westaw_project_number ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{project.westaw_project_number}</span>
                                    <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {proHubForm.westaw_project_number && (
                      <p className="text-xs text-slate-500">
                        Client Project: {proHubForm.project_number}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="supplier_cb">Supplier</Label>
                    <Select value={proHubForm.supplier_id} onValueChange={(value) => setProHubForm({ ...proHubForm, supplier_id: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_number_cb">Invoice Number</Label>
                      <Input
                        id="invoice_number_cb"
                        value={proHubForm.invoice_number}
                        onChange={(e) => setProHubForm({ ...proHubForm, invoice_number: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_cb">Date</Label>
                      <Input
                        id="date_cb"
                        type="date"
                        value={proHubForm.date}
                        onChange={(e) => setProHubForm({ ...proHubForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_value_cb">Invoice Value</Label>
                      <Input
                        id="invoice_value_cb"
                        type="number"
                        step="0.01"
                        value={proHubForm.invoice_value}
                        onChange={(e) => setProHubForm({ ...proHubForm, invoice_value: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="weight_cb">Weight</Label>
                      <Input
                        id="weight_cb"
                        type="number"
                        step="0.01"
                        value={proHubForm.weight}
                        onChange={(e) => setProHubForm({ ...proHubForm, weight: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dc_cb">DC</Label>
                    <Input
                      id="dc_cb"
                      value={proHubForm.dc}
                      onChange={(e) => setProHubForm({ ...proHubForm, dc: e.target.value })}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Recent Container Bau Records</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {proHubRecords.filter(r => r.type === 'container-bau').slice(0, 10).map((record) => (
                    <div key={record.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">WESTAW: {record.westaw_project_number}</p>
                          <p className="text-sm text-slate-600">Project: {record.project_number}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div>Supplier: {record.supplier_name}</div>
                        <div>Invoice: {record.invoice_number}</div>
                        <div>Value: ₹{record.invoice_value}</div>
                        <div>Weight: {record.weight}</div>
                        <div className="col-span-2">Date: {new Date(record.date).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                  {proHubRecords.filter(r => r.type === 'container-bau').length === 0 && (
                    <div className="text-center py-8 text-slate-600">No records found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Job Work Tab */}
        <TabsContent value="job-work">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Add Job Work Record</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleJobWorkSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>WESTAW Project</Label>
                    <Popover open={jobWorkProjectOpen} onOpenChange={setJobWorkProjectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={jobWorkProjectOpen}
                          className="w-full justify-between font-normal"
                          data-testid="jw-project-select"
                        >
                          <span className={jobWorkForm.westaw_project_number ? "text-slate-900" : "text-slate-500"}>
                            {getJobWorkProjectDisplay()}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search projects..." />
                          <CommandList>
                            <CommandEmpty>No project found.</CommandEmpty>
                            <CommandGroup>
                              {projects.map((project) => (
                                <CommandItem
                                  key={project.id}
                                  value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                                  onSelect={() => handleJobWorkProjectSelect(project)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      jobWorkForm.westaw_project_number === project.westaw_project_number ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{project.westaw_project_number}</span>
                                    <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dc_jw">DC</Label>
                      <Input
                        id="dc_jw"
                        data-testid="jw-dc-input"
                        value={jobWorkForm.dc}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, dc: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_jw">Date</Label>
                      <Input
                        id="date_jw"
                        data-testid="jw-date-input"
                        type="date"
                        value={jobWorkForm.date}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight_jw">Weight</Label>
                      <Input
                        id="weight_jw"
                        data-testid="jw-weight-input"
                        type="number"
                        step="0.01"
                        value={jobWorkForm.weight}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, weight: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invoice_jw">Invoice</Label>
                      <Input
                        id="invoice_jw"
                        data-testid="jw-invoice-input"
                        value={jobWorkForm.invoice}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, invoice: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="value_jw">Value</Label>
                      <Input
                        id="value_jw"
                        data-testid="jw-value-input"
                        type="number"
                        step="0.01"
                        value={jobWorkForm.value}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, value: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier_jw">Supplier</Label>
                      <Select value={jobWorkForm.supplier_id} onValueChange={(value) => setJobWorkForm({ ...jobWorkForm, supplier_id: value })} required>
                        <SelectTrigger data-testid="jw-supplier-select">
                          <SelectValue placeholder="Select a supplier" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="transporter_jw">Transporter Name</Label>
                    <Input
                      id="transporter_jw"
                      data-testid="jw-transporter-input"
                      placeholder="Enter transporter name"
                      value={jobWorkForm.transporter_name}
                      onChange={(e) => setJobWorkForm({ ...jobWorkForm, transporter_name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="transport_send_jw">Transport Expense (Send) ₹</Label>
                      <Input
                        id="transport_send_jw"
                        data-testid="jw-transport-send-input"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={jobWorkForm.transport_expense_send}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, transport_expense_send: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="transport_return_jw">Transport Expense (Return) ₹</Label>
                      <Input
                        id="transport_return_jw"
                        data-testid="jw-transport-return-input"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={jobWorkForm.transport_expense_return}
                        onChange={(e) => setJobWorkForm({ ...jobWorkForm, transport_expense_return: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="remarks_jw">Remarks</Label>
                    <Input
                      id="remarks_jw"
                      data-testid="jw-remarks-input"
                      value={jobWorkForm.remarks}
                      onChange={(e) => setJobWorkForm({ ...jobWorkForm, remarks: e.target.value })}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-job-work-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Recent Job Work Records</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {jobWorkRecords.slice(0, 10).map((record) => (
                    <div key={record.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">WESTAW: {record.westaw_project_number}</p>
                          <p className="text-sm text-slate-600">Supplier: {record.supplier_name}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div>Invoice: {record.invoice}</div>
                        <div>Value: ₹{record.value}</div>
                        <div>Weight: {record.weight}</div>
                        <div>DC: {record.dc}</div>
                        <div className="col-span-2">Date: {new Date(record.date).toLocaleDateString()}</div>
                        {record.transporter_name && (
                          <div className="col-span-2 text-sky-700">Transporter: {record.transporter_name}</div>
                        )}
                        {(record.transport_expense_send > 0 || record.transport_expense_return > 0) && (
                          <div className="col-span-2 text-amber-700">
                            Transport: Send ₹{record.transport_expense_send || 0} | Return ₹{record.transport_expense_return || 0}
                          </div>
                        )}
                        <div className="col-span-2">Remarks: {record.remarks}</div>
                      </div>
                    </div>
                  ))}
                  {jobWorkRecords.length === 0 && (
                    <div className="text-center py-8 text-slate-600">No records found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default MaterialReconciliation;
